import { EffectiveOnFieldFragment, OwnerFieldFragment } from 'generated/resources/graphql'

import { Employee } from 'modules/core/hooks/useCurrentSession'

import { getAdditionalInformationAttributes } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/AdditionalInformation/form/form'
import { getEffectiveOnFieldDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/BasicInformation/form/EffectiveOnField/form'
import { getOwnerFieldDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/BasicInformation/form/OwnerField/form'
import { getBudgetDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Budget/form/form'
import { getCategoryDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Category/form/form'
import { getCostCenterDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/CostCenters/form/form'
import { getEmployeeDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Employee/form'
import { getGroupDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Group/form/form'
import { getProjectsDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Projects/form/form'
import {
  type MileageInformationWithIntegerRate,
  getMileageInformationDefaultValues,
} from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/mileage/MileageInformation/form/form'
import { getRoundTripDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/mileage/RoundTrip/form/form'
import { getTripInformationAttributes } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/mileage/Trip/form/form'
import { getFilesDefaultValues } from 'modules/expenses/components/expenses/Detail/_newForm/Files/form/form'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { Expensable } from 'modules/expenses/types/Expensable'

export type MileageFormValues = ReturnType<typeof getMileageFormDefaultValues>

export interface GetMileageFormDefaultValuesParams {
  employee: Employee
  customizationFields: CustomizationField[]
  expensable?: Expensable
  groupId?: string | null
  groupCurrency?: string | null
}

export const getMileageFormDefaultValues = ({
  employee,
  customizationFields,
  groupId,
  expensable,
  groupCurrency,
}: GetMileageFormDefaultValuesParams) => {
  const ownerInformation: OwnerFieldFragment = {
    owner: {
      employeeName: expensable?.owner?.employeeName,
    },
  }
  const effectiveOnInformation: EffectiveOnFieldFragment = {
    effectiveOn: expensable?.effectiveOn || new Date().toISOString(),
  }
  const additionalInformation = {
    internalReference: expensable?.internalReference ?? '',
    description: expensable?.description ?? '',
  }
  const budgetInformation = {
    bugdetMovement: {
      budget: {
        id: expensable?.bugdetMovement?.budget?.id ?? '0',
      },
    },
  }

  const projectsInformation = {
    projects: [...(expensable?.projects || [])],
  }
  const categoryInformation = {
    mileage: {
      category: { id: expensable?.mileage?.category?.id ?? '' },
      subcategory: expensable?.mileage?.subcategory ?? '',
    },
  }

  const tripInformation = {
    mileage: {
      destinationLatitude: expensable?.mileage?.destinationLatitude,
      destinationLongitude: expensable?.mileage?.destinationLongitude,
      originLatitude: expensable?.mileage?.originLatitude,
      originLongitude: expensable?.mileage?.originLongitude,
      from: expensable?.mileage?.from,
      to: expensable?.mileage?.to,
      mileageStops: expensable?.mileage?.mileageStops ?? [],
    },
  }
  const roundTripInformation = {
    mileage: {
      roundTrip: expensable?.mileage?.roundTrip,
    },
  }

  const mileageInformation: MileageInformationWithIntegerRate = {
    amount: expensable?.amount ?? 0,
    currency: expensable?.currency || groupCurrency || employee?.legalEntity?.currency || 'EUR',
    mileage: {
      mileage: expensable?.mileage?.mileage,
      calculatedMileage: expensable?.mileage?.calculatedMileage,
      units: expensable?.mileage?.units,
      rate: expensable?.mileage?.rate ? parseFloat(expensable?.mileage?.rate) : null,
    },
  }

  const files = {
    mileage: {
      files: expensable?.mileage?.files || [],
    },
  }

  return {
    ...getEmployeeDefaultValues(employee, expensable),
    ...getOwnerFieldDefaultValues(ownerInformation),
    ...getAdditionalInformationAttributes(additionalInformation),
    ...getBudgetDefaultValues(budgetInformation),
    ...getProjectsDefaultValues(projectsInformation),
    ...getTripInformationAttributes(tripInformation),
    ...getFilesDefaultValues(files),
    ...getCategoryDefaultValues(categoryInformation),
    ...getEffectiveOnFieldDefaultValues(effectiveOnInformation),
    ...getRoundTripDefaultValues(roundTripInformation),
    ...getGroupDefaultValues(groupId),
    ...getMileageInformationDefaultValues(mileageInformation, employee),
    ...getCostCenterDefaultValues(expensable),
    originalMileageId: expensable?.mileage?.id ?? null,
    hasReuseTripOption: false,
  }
}
